import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Flex,
    SimpleGrid,
    Text,
    useMultiStyleConfig,
    Link as ChakraLink,
} from '@chakra-ui/react'
import {MenuCardGroup} from './menu-card-group'
import Link from '@salesforce/retail-react-app/app/components/link'
import {Link as RouteLink} from 'react-router-dom'
import {remCalc} from '../../theme/_utils'
import {getGlobalLink} from '../../utils/contentstack-utils'

const DrawerMenuSecondLevel = ({secondLevel, categoriesData}) => {
    const styles = useMultiStyleConfig('KenwoodMenu')

    if (secondLevel.layout === 'Category') {
        const group = secondLevel.standard_group[0]
        return (
            <SimpleGrid {...styles.secondLevelCategoryGrid}>
                {secondLevel?.show_explore_all_card && (
                    <ExploreAllButton
                        exploreAllCard={secondLevel?.explore_all_card}
                        categoriesData={categoriesData}
                    />
                )}
                <MenuCardGroup group={group} categoriesData={categoriesData} />

                {secondLevel.footer?.tertiary_links?.length > 0 ? (
                    <Flex {...styles.secondLevelFooter}>
                        {secondLevel?.footer?.explore_all_label && (
                            <Text {...styles.footerText} textAlign="center">
                                {secondLevel?.footer?.explore_all_label}
                            </Text>
                        )}
                        <Flex
                            gap={remCalc(16)}
                            pb={remCalc(24)}
                            justifyContent="center"
                            flexWrap="wrap"
                        >
                            {secondLevel.footer?.tertiary_links?.map((link) => {
                                return (
                                    <TertiaryLink
                                        key={link.uid}
                                        link={link}
                                        categoriesData={categoriesData}
                                    />
                                )
                            })}
                        </Flex>
                    </Flex>
                ) : (
                    <Box borderTop="small" borderColor="border.light" />
                )}
                <MenuCardGroup
                    group={secondLevel?.filters_group[0]}
                    categoriesData={categoriesData}
                    type="filter"
                />
            </SimpleGrid>
        )
    }
    if (secondLevel.layout === 'Promo/Gift') {
        return (
            <Flex {...styles.secondLevelContainer}>
                <MenuCardGroup
                    group={secondLevel.promos_group[0]}
                    categoriesData={categoriesData}
                    type="promo"
                    compact
                />
            </Flex>
        )
    }
}

DrawerMenuSecondLevel.displayName = 'DrawerMenuSecondLevel'

DrawerMenuSecondLevel.propTypes = {
    secondLevel: PropTypes.object,
    categoriesData: PropTypes.object,
    categoryUrl: PropTypes.string,
}

export {DrawerMenuSecondLevel}

const ExploreAllButton = ({exploreAllCard, categoriesData}) => {
    const styles = useMultiStyleConfig('KenwoodMenu')
    const {url, label, isAbsolute} = getGlobalLink({
        link: exploreAllCard?.redirects,
        categoriesData,
    })

    const content = (
        <Button variant="secondary" {...styles.exploreAllButton}>
            {label}
        </Button>
    )

    if (isAbsolute) {
        return (
            <ChakraLink href={url} isExternal>
                {content}
            </ChakraLink>
        )
    }
    return (
        <Link as={RouteLink} to={url}>
            {content}
        </Link>
    )
}

ExploreAllButton.displayName = 'ExploreAllButton'
ExploreAllButton.propTypes = {
    exploreAllCard: PropTypes.object.isRequired,
    categoriesData: PropTypes.object.isRequired,
}

const TertiaryLink = ({link, categoriesData}) => {
    const {url, label, isAbsolute} = getGlobalLink({link: link?.redirects, categoriesData})

    const content = (
        <Button
            variant="default"
            {...{
                w: {
                    base: remCalc(163),
                    md: remCalc(168),
                    lg: remCalc(213),
                },
                p: remCalc(16),
                textAlign: 'center',
                borderRadius: 'full',
                backgroundColor: 'background.light',
                textStyle: 'head-xxsmall',
                textDecoration: 'underline',
                color: 'text.default',
                display: 'flex',
                whiteSpace: 'wrap',
            }}
        >
            {label}
        </Button>
    )

    if (isAbsolute) {
        return (
            <ChakraLink href={url} isExternal>
                {content}
            </ChakraLink>
        )
    }
    return (
        <Link as={RouteLink} to={url}>
            {content}
        </Link>
    )
}

TertiaryLink.displayName = 'TertiaryLink'

TertiaryLink.propTypes = {
    link: PropTypes.object.isRequired,
    categoriesData: PropTypes.object.isRequired,
}
